import { useEffect, useRef, useState } from "react";
import { Camera, Lock, X, UserCog, ShieldCheck, Check, Pencil } from "lucide-react";
import { toast } from "sonner";
import { api, fmtErr, mediaUrl } from "../lib/api";
import { useAuth } from "../context/AuthContext";

export default function ProfileEdit() {
  const { user, refresh } = useAuth();
  const [form, setForm] = useState({ name: "", birth_date: "", birth_time: "", birth_place: "" });
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [editing, setEditing] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [emailFlow, setEmailFlow] = useState(0); // 0 chiuso, 1 form, 2 inviata
  const [newEmail, setNewEmail] = useState("");
  const [password, setPassword] = useState("");
  const [emailBusy, setEmailBusy] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (user) {
      setForm({
        name: user.name || "",
        birth_date: user.birth_date || "",
        birth_time: user.birth_time || "",
        birth_place: user.birth_place || "",
      });
      setEditing(false);
      setDirty(false);
    }
  }, [user]);

  const upd = (k) => (e) => {
    setForm((f) => ({ ...f, [k]: e.target.value }));
    setDirty(true);
  };

  const cancelEdit = () => {
    setForm({
      name: user.name || "",
      birth_date: user.birth_date || "",
      birth_time: user.birth_time || "",
      birth_place: user.birth_place || "",
    });
    setEditing(false);
    setDirty(false);
  };

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/profile", form);
      await refresh();
      setSaved(true);
      setEditing(false);
      setDirty(false);
      window.setTimeout(() => setSaved(false), 3200);
      toast.success("Profilo aggiornato.");
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setBusy(false);
    }
  };

  const onPhoto = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    setBusy(true);
    try {
      await api.post("/profile/photo", fd);
      await refresh();
      toast.success("Immagine del profilo aggiornata.");
    } catch (err) {
      toast.error(fmtErr(err));
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const requestEmailChange = async () => {
    setEmailBusy(true);
    try {
      await api.post("/profile/email/request", { new_email: newEmail, password });
      setEmailFlow(2);
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setEmailBusy(false);
    }
  };

  if (!user) return null;

  const birthLabel = [
    user.birth_date ? new Date(`${user.birth_date}T00:00:00`).toLocaleDateString("it-IT", { day: "numeric", month: "long", year: "numeric" }) : null,
    user.birth_time,
    user.birth_place,
  ].filter(Boolean).join(" · ");

  const photoCircle = (
    <div className="w-12 h-12 rounded-full border border-amber-500/40 bg-[#1a0b2b] overflow-hidden flex items-center justify-center shrink-0">
      {user.photo_url ? (
        <img src={mediaUrl(user.photo_url)} alt="Foto profilo" className="w-full h-full object-cover" data-testid="profile-photo-preview" />
      ) : (
        <span className="font-display text-xl text-amber-300">{(user.name || "?").charAt(0)}</span>
      )}
    </div>
  );

  const photoBtn = (
    <label className="cursor-pointer inline-flex items-center gap-1.5 text-[11px] text-amber-300/80 hover:text-amber-200 transition-colors" data-testid="profile-photo-btn">
      <Camera className="w-3 h-3" /> Cambia immagine
      <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onPhoto} data-testid="profile-photo-input" />
    </label>
  );

  return (
    <div className="mb-10" data-testid="profile-edit-section">
      <div className="flex items-center gap-2 mb-4">
        <h2 className="font-cinzel text-xs tracking-[0.25em] uppercase text-amber-400 flex items-center gap-2">
          <UserCog className="w-4 h-4" /> Profilo
        </h2>
        {!editing && (
          <button
            data-testid="profile-edit-btn"
            onClick={() => setEditing(true)}
            aria-label="Modifica profilo"
            title="Modifica profilo"
            className="p-1 text-white/70 hover:text-white transition-colors"
          >
            <Pencil className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      {!editing ? (
        <div className="space-y-2.5">
          <div className="flex items-center gap-4">
            {photoCircle}
            <div className="min-w-0">
              <p className="text-amber-100 font-medium truncate" data-testid="profile-name-view">{user.name}</p>
              <p className="text-xs text-purple-300/60 truncate" data-testid="profile-email-readonly">{user.email}</p>
              {birthLabel && <p className="text-xs text-purple-300/50 mt-0.5" data-testid="profile-birth-view">{birthLabel}</p>}
            </div>
          </div>
          <div className="flex items-center gap-5">
            {photoBtn}
            <button
              data-testid="change-email-btn"
              onClick={() => { setEmailFlow(1); setNewEmail(""); setPassword(""); }}
              className="inline-flex items-center gap-1.5 text-[11px] text-amber-300/80 hover:text-amber-200 transition-colors"
            >
              <Lock className="w-3 h-3" /> Cambia email
            </button>
          </div>
          {saved && (
            <p data-testid="profile-save-success-banner" className="text-xs text-emerald-300">
              Profilo aggiornato con successo
            </p>
          )}
        </div>
      ) : (
        <div className="space-y-4" data-testid="profile-edit-form">
          <div className="flex items-center gap-4">
            {photoCircle}
            {photoBtn}
          </div>
          <div>
            <label className="text-xs text-purple-200/70 uppercase tracking-widest">Nome</label>
            <input
              data-testid="profile-name-input"
              value={form.name}
              maxLength={80}
              onChange={upd("name")}
              className="input-dark w-full mt-1.5"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs text-purple-200/70 uppercase tracking-widest">Data di nascita</label>
              <input
                data-testid="profile-birthdate-input"
                type="date"
                value={form.birth_date}
                onChange={upd("birth_date")}
                className="input-dark w-full mt-1.5"
              />
            </div>
            <div>
              <label className="text-xs text-purple-200/70 uppercase tracking-widest">Ora di nascita</label>
              <input
                data-testid="profile-birthtime-input"
                type="time"
                value={form.birth_time}
                onChange={upd("birth_time")}
                className="input-dark w-full mt-1.5"
              />
            </div>
          </div>
          <div>
            <label className="text-xs text-purple-200/70 uppercase tracking-widest">Luogo di nascita</label>
            <input
              data-testid="profile-birthplace-input"
              value={form.birth_place}
              maxLength={80}
              onChange={upd("birth_place")}
              className="input-dark w-full mt-1.5"
            />
          </div>
          <div className="flex gap-3">
            <button data-testid="profile-cancel-btn" onClick={cancelEdit} disabled={busy} className="flex-1 py-2.5 text-sm rounded-lg border border-[#4a2175] text-purple-200/80 hover:border-amber-500/40 transition-colors disabled:opacity-40">
              Annulla
            </button>
            <button data-testid="profile-save-btn" onClick={save} disabled={busy || !dirty || !form.name.trim()} className="gold-btn flex-1 py-2.5 text-sm disabled:opacity-40 inline-flex items-center justify-center gap-2">
              {busy ? "Salvataggio…" : saved ? (<><Check className="w-4 h-4" /> Salvato</>) : "Salva modifiche"}
            </button>
          </div>
        </div>
      )}

      {emailFlow > 0 && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setEmailFlow(0)}>
          <div className="w-full max-w-sm rounded-2xl border border-[#4a2175] bg-[#150920] p-6" onClick={(e) => e.stopPropagation()} data-testid="change-email-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display text-lg text-amber-100">Cambio email sicuro</h3>
              <button data-testid="change-email-close" onClick={() => setEmailFlow(0)} className="p-1 text-purple-300/70 hover:text-purple-100">
                <X className="w-4 h-4" />
              </button>
            </div>

            {emailFlow === 1 && (
              <div className="space-y-4">
                <p className="text-xs text-purple-200/70 leading-relaxed">
                  Inserisci la nuova email e conferma con la tua password. Invieremo un link di verifica alla nuova email: il cambio diventa attivo solo dopo la conferma. Tutti i tuoi dati restano collegati allo stesso account.
                </p>
                <div>
                  <label className="text-xs text-purple-200/70 uppercase tracking-widest">Nuova email</label>
                  <input data-testid="new-email-input" type="email" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} className="input-dark w-full mt-1.5" placeholder="nuova@email.it" />
                </div>
                <div>
                  <label className="text-xs text-purple-200/70 uppercase tracking-widest">Password attuale</label>
                  <input data-testid="confirm-password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="input-dark w-full mt-1.5" placeholder="••••••••" />
                </div>
                <button
                  data-testid="send-verification-btn"
                  disabled={!newEmail.includes("@") || !password || emailBusy}
                  onClick={requestEmailChange}
                  className="gold-btn w-full py-3 text-sm disabled:opacity-40"
                >
                  {emailBusy ? "Invio in corso…" : "Invia verifica alla nuova email"}
                </button>
              </div>
            )}

            {emailFlow === 2 && (
              <div className="text-center space-y-4">
                <ShieldCheck className="w-10 h-10 text-amber-300 mx-auto" />
                <p className="text-sm text-purple-100/80 leading-relaxed" data-testid="change-email-sent">
                  Abbiamo inviato un link di conferma a <strong className="text-amber-100">{newEmail}</strong>. Fino alla conferma, il tuo account resta collegato a {user.email}.
                </p>
                <button data-testid="change-email-done" onClick={() => setEmailFlow(0)} className="gold-btn px-8 py-3 text-sm">Ho capito</button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
